import { events } from '../../util/Events';
import { Examentraining } from '../../models/Examentraining';
import { Article } from '../../models/Article';
import { cartItemIsArticle, cartItemIsTraining } from '../../models/type.functions';
import { post } from '../../util/ApiWrapper';
import { inSchoolCartService } from '../SearchSchool/InSchoolCart/InSchoolCart.service';
import $ from 'jquery';

// Events emitted on search results- /shop page.
export const CART_ITEM_REMOVED = 'cartItemRemoved';
export const CART_ITEM_ADDED = 'cartItemAdded';
// Event emitted on checkout page.
export const CART_UPDATED = 'cartUpdated';

type CartItems = (Examentraining|Article)[]|null;

// variables set in twig
declare const cartExt: string[]|null;
declare const cartShopItems: {id_item: string}[]|null;

/**
 * This class handles getting and storing the carts contents.
 * This is the only central location where there calls are allowed
 * This is also the only locations where the cart events are emitted
 * Changing the DOM should be handled in the react components
 */
class Cart {

  /**
   * Get the array of trainingIds in the cart session set by Twig in base.twig.
   * By exposing the ids via twig, we do not have to do a separate call, to
   * expose the ids to the typescript.
   *
   * @private - public for testing
   */
  public cartSession: string[]|null = cartExt || [];

  /**
   * Articles that can be bought separate from a training.
   */
  public shopItems: {id_item: string}[]|null = cartShopItems || [];

  constructor() {
    this.syncSessionAndLocalStorageTraining();
    this.syncSessionAndLocalStorageForItems();
  }

  /**
     * The cart differs from the InschoolCart because this cart has
     * no direct access to all trainings and the normal cart will exist
     * along multiple pages
     */
  public syncSessionAndLocalStorageTraining(): void {
    let sessionTrainingIds: string[] = this.cartSession as string[];
    let shoppingCart = this.get();

    let storageTrainings: Examentraining[] = shoppingCart
      .filter(item => !!item)
      .filter(item => cartItemIsTraining(item)) as Examentraining[];

    // filter trainings in localstorage that are not in the session
    storageTrainings
      .filter(training => !!training)
      .filter(training => !sessionTrainingIds.includes(training.id_training))
      .map(training => this.removeItemFromLocalStorage(training.id_training));

    // check which session ids are not in the localstorage
    let storageIds: string[] = storageTrainings
      .map((training: Examentraining) => training.id_training);

    const unCachedTrainingIds: string[] = sessionTrainingIds.filter(id => !storageIds.includes(id));

    // get training object from backend of missing trainings
    unCachedTrainingIds.map((trainingId: string) => this.getTrainingObject(trainingId));
  }

  /**
     * The cart differs from the InschoolCart because this cart has
     * no direct access to all trainings and the normal cart will exist
     * along multiple pages
     */
  public syncSessionAndLocalStorageForItems(): void {
    let sessionItemIds: string[] = this.shopItems.map(item => item.id_item);
    let shoppingCart = this.get();

    let storageItems: Article[] = shoppingCart
      .filter(item => !!item)
      .filter(item => cartItemIsArticle(item)) as Article[];

    // Remove items in localstorage that are not in the session
    storageItems
      .filter(item => !!item)
      .filter(item => !sessionItemIds.includes(item.id_item))
      .map(item => this.removeItemFromLocalStorage(item.id_item));

    // check which session ids are not in the localstorage
    let storedIds: string[] = storageItems
      .map((training: Article) => training.id_item);

    const unCachedItemIds: string[] = sessionItemIds.filter(id => !storedIds.includes(id));

    // get training object from backend of missing trainings
    unCachedItemIds.map((itemId: string) => this.getItemObject(itemId));
  }

  /**
   * Gets the carts content
   */
  public get(): CartItems {
    if (localStorage) {
      const localCart = localStorage.getItem('cart');
      return localCart ? JSON.parse(localCart) : [];
    }

    return [];
  }

  /**
   * Store the cart in the localStorage, so we do not need to query
   * the database each time we visit a page.
   */
  private set(cartTrainings: CartItems): void {
    localStorage.setItem('cart', JSON.stringify(cartTrainings));
  }

  /**
     * Empties the cart in the localstorage
     */
  public empty(): void {
    localStorage.removeItem('cart');
  }

  /**
   * Get the entire training object from the backend and store it in
   * the localstorage so the cart can use its data
   */
  public async getTrainingObject(trainingId: string): Promise<void> {
    const data: { training: Examentraining|null } = await post({
      controller: 'Cart',
      method: 'getTrainingObject',
      data: {
        trainingId: trainingId,
      },
    });

    this.addItemToLocalStorage(data.training);
  }

  /**
   * Get the entire item object from the backend and store it in
   * the localstorage so the cart can use its data
   */
  public async getItemObject(itemId: string): Promise<void> {
    const data: { item: Article|null } = await post({
      controller: 'Cart',
      method: 'getItemObject',
      data: {
        itemId: itemId,
      },
    });

    this.addItemToLocalStorage(data.item);
  }

  /**
   * Adds a sub item, like `oefenopgaven` to a training
   */
  public async addShopItem(item: Article): Promise<void> {
    await post({
      controller: 'Cart',
      method: 'addShopItem',
      data: {
        itemId: item.id_item,
      },
    });

    this.addItemToLocalStorage(item);
  }

  /**
   * Removes a sub item, like `oefenopgaven` from a training
   */
  public async removeShopItem(itemId: string): Promise<void> {
    await post({
      controller: 'Cart',
      method: 'removeShopItem',
      data: {
        itemId: itemId,
      },
    });

    this.removeItemFromLocalStorage(itemId);
  }


  /**
   * Adds a sub item, like `oefenopgaven` to a training
   */
  public async addArticle(trainingId: string, articleId: string): Promise<void> {
    await post({
      controller: 'Cart',
      method: 'addAddon',
      data: {
        TrainingId: trainingId,
        ItemId: articleId,
      },
    });

    events.emit(CART_UPDATED);
  }

  /**
   * Removes a sub item, like `oefenopgaven` from a training
   */
  public async removeArticle(trainingId: string, articleId: string): Promise<void> {
    await post({
      controller: 'Cart',
      method: 'removeAddon',
      data: {
        removeItem: articleId,
        trainingId: trainingId,
      },
    });

    events.emit(CART_UPDATED);
  }

  /**
   * Adds examentraining to the shopping cart
   */
  public async addTraining(training: Examentraining): Promise<void> {
    const result = await post({
      controller: 'Cart',
      method: 'addTraining',
      data: {
        AddProduct: training.id_training,
      },
    });

    if(result['message']){
        $('#overlay')
        .fadeIn(300)
        .html('<div class="overlay-container" style="background-color: #74A0A3"><br><p>' + result['message'] + '</p><br></div>');
    } else {
      this.addItemToLocalStorage(training);
    }
  }

  /**
   * This is a temporary function because it only adds the trainingId to the php session
   * and not to the local storage. The upsell should be able to send the training object
   * @deprecated
   */
  public async addTrainingId(trainingId: string): Promise<void> {
    await post({
      controller: 'Cart',
      method: 'addTraining',
      data: {
        AddProduct: trainingId,
      },
    });
  }

  /**
   * Removes training from the shopping cart
   */
  public async removeTraining(trainingId: string): Promise<void> {
    await post({
      controller: 'Cart',
      method: 'removeTraining',
      data: {
        RemoveProduct: trainingId,
      },
    });
    await inSchoolCartService.removeTraining(trainingId);
    this.removeItemFromLocalStorage(trainingId);
  }

  /**
   * Adds the training to the cart array in the local storage
   */
  public addItemToLocalStorage(training: Examentraining|Article|null): void {
    if (!training) {
      return;
    }

    let cartTraining = this.get();
    cartTraining.push(training);
    this.set(cartTraining);

    events.emit(CART_ITEM_ADDED, training);
  }

  /**
   * Searches the cart content and removes the training with the matching id
   */
  public removeItemFromLocalStorage(removeId: string): void {
    let cartListItems = this.get();

    let updatedCart = cartListItems
      .filter(item => !!item)
      .filter((item: Examentraining|Article) => {
        if (cartItemIsArticle(item)) {
          return item.id_item !== removeId;
        } else if (cartItemIsTraining(item)) {
          return item.id_training !== removeId;
        }
      });

    this.set(updatedCart);

    events.emit(CART_ITEM_REMOVED);
    events.emit(CART_UPDATED);
  }

}

export let cart = new Cart();

